import { NavLink, useLocation } from 'react-router';
import { useTDAccess } from '../../hooks/useTDAccess';
import { useAuth } from '../../contexts/AuthContext';

export interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  /** 'exact' highlights only on an exact path match; 'prefix' on any subpath. */
  match: 'exact' | 'prefix';
}

// =============================================================================
// Icons
// =============================================================================

function NavIcon({ d }: { d: string }) {
  return (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
    </svg>
  );
}

const ICON = {
  home: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  manage: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
  admin: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  overview: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z',
  matches: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
  standings: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  bracket: 'M4 6h4v4H4V6zm0 8h4v4H4v-4zm12-8h4v4h-4V6zm0 8h4v4h-4v-4zM8 8h4v2H8V8zm0 8h4v2H8v-2zm4-6v4m0 4v4',
  players: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
  config: 'M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4',
} as const;

// =============================================================================
// Global (non-tournament) nav items
// =============================================================================

const homeNavItem: NavItem = {
  to: '/',
  label: 'Home',
  match: 'exact',
  icon: <NavIcon d={ICON.home} />,
};

const tdNavItem: NavItem = {
  to: '/td',
  label: 'Manage',
  match: 'prefix',
  icon: <NavIcon d={ICON.manage} />,
};

/**
 * Admin nav item - only shown to admin users.
 * Requirements: 6.1, 6.6
 */
const adminNavItem: NavItem = {
  to: '/admin',
  label: 'Admin',
  match: 'prefix',
  icon: <NavIcon d={ICON.admin} />,
};

// =============================================================================
// Tournament-scoped nav (pure helpers — unit-tested in BottomNav.test.ts)
// =============================================================================

export type TournamentContext =
  | { kind: 'player' | 'td'; id: string }
  | { kind: 'public'; slug: string };

/**
 * Detect which tournament a route is "inside", so the bottom nav can switch
 * from global tabs to that tournament's own tabs.
 * - `/td/tournament/:id/*` -> TD context (excludes the `/td/tournament/new` page)
 * - `/tournament/:id/*`    -> player context
 * - `/t/:slug`             -> public hub (slug-based; id resolved from cache)
 */
export function getTournamentContext(pathname: string): TournamentContext | null {
  const td = pathname.match(/^\/td\/tournament\/([^/]+)/);
  if (td && td[1] !== 'new') return { kind: 'td', id: td[1] };
  const player = pathname.match(/^\/tournament\/([^/]+)/);
  if (player) return { kind: 'player', id: player[1] };
  const pub = pathname.match(/^\/t\/([^/]+)/);
  if (pub) return { kind: 'public', slug: pub[1] };
  return null;
}

/**
 * Player/spectator tabs while inside a tournament: just Home and the hub.
 *
 * Matches and Standings used to be tabs here, each a separate lazily-loaded
 * route with its own fetch. They're sections of the hub now, reached by its
 * sticky chips — leaving them in the nav would put the same four destinations
 * on screen twice, one set of which reloads the page to reach them.
 */
export function buildPlayerTournamentNav(id: string): NavItem[] {
  return [
    { to: '/', label: 'Home', match: 'exact', icon: <NavIcon d={ICON.home} /> },
    {
      to: `/tournament/${id}`,
      label: 'Overview',
      match: 'exact',
      icon: <NavIcon d={ICON.overview} />,
    },
  ];
}

/** The same two tabs on the public hub, where the slug is what we have. */
export function buildPublicTournamentNav(slug: string): NavItem[] {
  return [
    { to: '/', label: 'Home', match: 'exact', icon: <NavIcon d={ICON.home} /> },
    { to: `/t/${slug}`, label: 'Overview', match: 'exact', icon: <NavIcon d={ICON.overview} /> },
  ];
}

/** TD tabs while inside a tournament. Home escapes to the TD home. */
export function buildTdTournamentNav(id: string): NavItem[] {
  const base = `/td/tournament/${id}`;
  return [
    { to: '/td', label: 'Home', match: 'exact', icon: <NavIcon d={ICON.home} /> },
    { to: base, label: 'Overview', match: 'exact', icon: <NavIcon d={ICON.overview} /> },
    { to: `${base}/players`, label: 'Players', match: 'prefix', icon: <NavIcon d={ICON.players} /> },
    { to: `${base}/bracket`, label: 'Bracket', match: 'prefix', icon: <NavIcon d={ICON.bracket} /> },
    { to: `${base}/config`, label: 'Config', match: 'prefix', icon: <NavIcon d={ICON.config} /> },
  ];
}

export function isNavItemActive(pathname: string, item: NavItem): boolean {
  return item.match === 'exact'
    ? pathname === item.to
    : pathname.startsWith(item.to);
}

// =============================================================================
// Component
// =============================================================================

/**
 * Bottom navigation bar for mobile-first navigation.
 * Fixed at bottom of viewport with safe area insets for PWA.
 *
 * Context-aware:
 * - Inside a tournament (`/tournament/:id/*` or `/td/tournament/:id/*`), shows
 *   that tournament's tabs (Overview / Matches / Standings / Bracket, or the TD
 *   equivalent) with a Home tab that escapes back to the list.
 * - Otherwise shows the global tabs: Home, Manage (club owners), Admin (admins).
 *
 * Note: Profile access is via the header icon (top-right).
 */
export function BottomNav() {
  const { hasTDAccess, isLoading } = useTDAccess();
  const { isAdmin } = useAuth();
  const location = useLocation();

  const ctx = getTournamentContext(location.pathname);

  let navItems: NavItem[];
  if (ctx?.kind === 'player') {
    navItems = buildPlayerTournamentNav(ctx.id);
  } else if (ctx?.kind === 'td') {
    navItems = buildTdTournamentNav(ctx.id);
  } else if (ctx?.kind === 'public') {
    navItems = buildPublicTournamentNav(ctx.slug);
  } else {
    navItems = [homeNavItem];
    if (hasTDAccess && !isLoading) navItems.push(tdNavItem);
    if (isAdmin) navItems.push(adminNavItem);
  }

  return (
    // Reserve the iOS home-indicator strip explicitly. The inline style is more
    // robust than the utility class (which was being clipped on installed PWAs),
    // and the small floor keeps a tidy gap on devices that report no inset.
    <nav
      className="flex-shrink-0 bg-bbx-dark-800/95 backdrop-blur-sm border-t border-bbx-dark-700"
      style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 0.5rem)' }}
    >
      <div className="flex items-center justify-around h-16">
        {navItems.map((item) => {
          const isActive = isNavItemActive(location.pathname, item);

          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.match === 'exact'}
              className={`flex flex-col items-center justify-center gap-1 flex-1 min-w-0 min-h-tap px-1 py-2 rounded-lg transition-colors ${
                isActive
                  ? 'text-bbx-neon-blue'
                  : 'text-bbx-dark-400 hover:text-bbx-dark-200'
              }`}
              aria-label={item.label}
            >
              {item.icon}
              <span className="text-xs font-medium">{item.label}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
